import { Button, TextArea } from '@calizahq/react-components-v2'
import { fromZonedTime, toZonedTime } from 'date-fns-tz'
import { Form, Formik } from 'formik'
import React, { FC, useMemo, useState } from 'react'

import { DateTimeInputControlled, InputSelect } from '@/components'
import { ScheduledCommunicationMutations } from '@/graphql/mutations/scheduledCommunication.mutations'
import { useGraphqlMutation } from '@/hooks/useGraphql'
import { ScheduledCommunicationList, ScheduledCommunicationStatus } from '@/types/ScheduledCommunication'

import { EditScheduledCommunicationFormValuesType, formSchema } from './EditScheduledCommunication.util'

type Props = {
  refetchOnSuccess: () => void
  scheduledCommunication: ScheduledCommunicationList
  toggle: () => void
}

export const EditScheduledCommunicationForm: FC<Props> = ({ scheduledCommunication, toggle, refetchOnSuccess }) => {
  const [error, setError] = useState<string>()

  const scheduledCommunicationStatusOptions = Object.keys(ScheduledCommunicationStatus).map((key) => {
    return { label: ScheduledCommunicationStatus[key], value: ScheduledCommunicationStatus[key] }
  })

  const { mutate: update, isPending: isUpdateLoading } = useGraphqlMutation(
    ScheduledCommunicationMutations.updateScheduledCommunication(),
    {
      onSuccess: () => {
        refetchOnSuccess && refetchOnSuccess()
        toggle && toggle()
      },
      onError: () => {
        setError('Error updating Scheduled Communication')
      },
    },
  )

  const timezone =
    scheduledCommunication.reservation.home.property.market.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone

  const scheduledDatetime = useMemo(() => {
    return toZonedTime(scheduledCommunication.scheduledDatetime, timezone)
  }, [scheduledCommunication, timezone])

  const handleUpdate = (values: EditScheduledCommunicationFormValuesType) => {
    update({
      scheduledCommunication: {
        scheduledCommunicationId: scheduledCommunication.id,
        scheduledDatetime: fromZonedTime(values.scheduledDatetime, timezone),
        content: values.content,
        status: values.status,
        cancelledReason: values.status === ScheduledCommunicationStatus.Cancelled ? values.cancelledReason : undefined,
      },
    })
  }

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          content: scheduledCommunication && scheduledCommunication.content,
          status: scheduledCommunication && scheduledCommunication.status,
          cancelledReason: '',
          scheduledDatetime: scheduledDatetime,
        }}
        onSubmit={handleUpdate}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={formSchema}
      >
        {({ values }) => {
          return (
            <Form className="flex min-h-140 w-full flex-col flex-wrap px-6 md:max-w-120">
              <p className="mb-10 font-bold text-header-sm leading-none">
                Destination: {scheduledCommunication.destination}
              </p>
              <div className="flex w-full flex-col md:mb-4 md:grid md:grid-cols-1 md:gap-4">
                <DateTimeInputControlled
                  className="mb-4 w-full"
                  endHour={18}
                  label="Scheduled Datetime"
                  name="scheduledDatetime"
                  startHour={8}
                  timeIntervals={30}
                  timeZone={timezone}
                />
              </div>
              <div className="flex w-full flex-col md:mb-4 md:grid md:grid-cols-1 md:gap-4">
                <InputSelect
                  className=" mb-4 w-full md:mb-0 md:mr-4"
                  id="status"
                  label="Status"
                  name="status"
                  options={scheduledCommunicationStatusOptions}
                />
              </div>
              {scheduledCommunication.status !== ScheduledCommunicationStatus.Cancelled &&
              values.status === ScheduledCommunicationStatus.Cancelled ? (
                <TextArea className="mb-4 h-fit-content w-full" label="Reason" name="cancelledReason" />
              ) : null}
              <TextArea className="mb-4 h-fit-content w-full" label="Content" maxLength={1000} name="content" />
              <div>{error && <p className=" mb-4 text-p-sm text-text-semantic-error">{error}</p>}</div>
              <div className="mt-4 flex justify-end md:flex-row	">
                <Button
                  className="mb-2 w-full bg-text-semantic-success md:mb-0 md:w-auto"
                  disabled={isUpdateLoading}
                  isLoading={isUpdateLoading}
                  size="small"
                  type="submit"
                >
                  Update
                </Button>
              </div>
            </Form>
          )
        }}
      </Formik>
    </>
  )
}
